import React, { useState, useEffect } from 'react';
import { Layout as AntLayout, Menu, Button, Drawer, Dropdown, Row, Col } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { 
  HomeOutlined, 
  InfoCircleOutlined, 
  FileTextOutlined, 
  SafetyCertificateOutlined, 
  QuestionCircleOutlined, 
  PhoneOutlined,
  MenuOutlined,
  SafetyOutlined,
  MailOutlined,
  WhatsAppOutlined,
  AppleOutlined,
  AndroidOutlined
} from '@ant-design/icons';

const { Header, Content, Footer } = AntLayout;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const location = useLocation();

  // Scroll to top when route changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, [location.pathname]);

  const handleMenuClick = () => {
    setMobileMenuVisible(false);
  };

  // Policy dropdown items: Refund Policy at top level; IOS submenu with Terms and Privacy
  const policyMenuItems = [
    {
      key: 'terms',
      icon: <FileTextOutlined />,
      label: (
        <Link to="/terms-and-conditions" onClick={handleMenuClick}>
          Terms and Conditions
        </Link>
      ),
    },
    {
      key: 'refund',
      icon: <FileTextOutlined />,
      label: (
        <Link to="/refund-policy" onClick={handleMenuClick}>
          Refund Policy
        </Link>
      ),
    },
    {
      key: 'ios',
      icon: <AppleOutlined />,
      label: 'IOS',
      children: [
        {
          key: 'privacy-ios',
          icon: <FileTextOutlined />,
          label: (
            <Link to="/privacy-policy" onClick={handleMenuClick}>
              Privacy Policy - IOS
            </Link>
          ),
        },
      ],
    },
    {
      key: 'android',
      icon: <AndroidOutlined />,
      label: 'Android',
      children: [
        {
          key: 'privacy-android',
          icon: <FileTextOutlined />,
          label: (
            <Link to="/privacy-policy-android" onClick={handleMenuClick}>
              Privacy Policy - Android
            </Link>
          ),
        },
      ],
    },
  ];

  // Mobile: flat list so all policy links are visible (no nested IOS/Android)
  const mobilePolicyMenuItems = [
    {
      key: 'terms',
      icon: <FileTextOutlined />,
      label: (
        <Link to="/terms-and-conditions" onClick={handleMenuClick}>
          Terms and Conditions
        </Link>
      ),
    },
    {
      key: 'refund',
      icon: <FileTextOutlined />,
      label: (
        <Link to="/refund-policy" onClick={handleMenuClick}>
          Refund Policy
        </Link>
      ),
    },
    {
      key: 'privacy-ios',
      icon: <AppleOutlined />,
      label: (
        <Link to="/privacy-policy" onClick={handleMenuClick}>
          Privacy Policy - IOS
        </Link>
      ),
    },
    {
      key: 'privacy-android',
      icon: <AndroidOutlined />,
      label: (
        <Link to="/privacy-policy-android" onClick={handleMenuClick}>
          Privacy Policy - Android
        </Link>
      ),
    },
  ];

  const menuItems = [
    {
      key: '/',
      icon: <HomeOutlined />,
      label: <Link to="/" onClick={handleMenuClick}>Home</Link>,
    },
    {
      key: '/about',
      icon: <InfoCircleOutlined />,
      label: <Link to="/about" onClick={handleMenuClick}>About Us</Link>,
    },
    {
      key: '/policy',
      icon: <FileTextOutlined />,
      label: (
        <Dropdown
          menu={{ items: policyMenuItems }}
          placement="bottomLeft"
          trigger={['hover']}
        >
          <span style={{ cursor: 'pointer' }}>
            Policy <span style={{ fontSize: '12px' }}>▼</span>
          </span>
        </Dropdown>
      ),
    },
    {
      key: '/lending-license',
      icon: <SafetyCertificateOutlined />,
      label: <Link to="/lending-license" onClick={handleMenuClick}>License</Link>,
    },
    {
      key: '/faqs',
      icon: <QuestionCircleOutlined />,
      label: <Link to="/faqs" onClick={handleMenuClick}>FAQs</Link>,
    },
    {
      key: '/contact',
      icon: <PhoneOutlined />,
      label: <Link to="/contact" onClick={handleMenuClick}>Contact Us</Link>,
    },
  ];

  return (
    <AntLayout>
      <Header style={{ 
        position: 'fixed', 
        zIndex: 1, 
        width: '100%',
        background: '#fff',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div className="header-inner" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 20px'
        }}>
          <div className="header-logo" style={{ 
            fontSize: '24px', 
            fontWeight: 'bold', 
            color: '#1890ff' 
          }}>
            JEJELY ULTIMATE LIMITED
          </div>
          
          {/* Desktop Menu - hidden on mobile */}
          <Menu
            className="desktop-menu"
            theme="light"
            mode="horizontal"
            selectedKeys={[location.pathname]}
            items={menuItems}
            style={{ 
              flex: 1, 
              justifyContent: 'flex-end',
              border: 'none'
            }}
          />
          
          {/* Mobile Menu Button - shown only on mobile via CSS */}
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setMobileMenuVisible(true)}
            className="mobile-menu-button"
          />
        </div>
      </Header>

      {/* Mobile Drawer */}
      <Drawer
        title="Menu"
        placement="right"
        onClose={() => setMobileMenuVisible(false)}
        open={mobileMenuVisible}
        width={280}
        className="mobile-drawer"
        styles={{ body: { padding: '0 16px 24px', overflowY: 'auto', maxHeight: 'calc(100vh - 55px)' } }}
      >
        <Menu
          mode="vertical"
          selectedKeys={[location.pathname]}
          items={[
            {
              key: '/',
              icon: <HomeOutlined />,
              label: <Link to="/" onClick={handleMenuClick}>Home</Link>,
            },
            {
              key: '/about',
              icon: <InfoCircleOutlined />,
              label: <Link to="/about" onClick={handleMenuClick}>About Us</Link>,
            },
            { type: 'divider' },
            {
              key: 'policy-label',
              icon: <FileTextOutlined />,
              label: 'Policy',
              disabled: true,
              style: { color: 'rgba(0,0,0,0.45)', fontSize: '12px', cursor: 'default' },
            },
            ...mobilePolicyMenuItems,
            { type: 'divider' },
            {
              key: '/lending-license',
              icon: <SafetyCertificateOutlined />,
              label: <Link to="/lending-license" onClick={handleMenuClick}>Lending License</Link>,
            },
            {
              key: '/faqs',
              icon: <QuestionCircleOutlined />,
              label: <Link to="/faqs" onClick={handleMenuClick}>FAQs</Link>,
            },
            {
              key: '/contact',
              icon: <PhoneOutlined />,
              label: <Link to="/contact" onClick={handleMenuClick}>Contact Us</Link>,
            },
          ]}
          style={{ border: 'none' }}
        />
      </Drawer>

      <Content className="main-content" style={{ marginTop: 64 }}>
        {children}
      </Content>

      <Footer className="site-footer" style={{ 
        background: '#001529', 
        color: 'white', 
        padding: '60px 20px 20px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <Row gutter={[60, 40]}>
            {/* Company Info */}
            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ color: '#1890ff', marginBottom: '15px', fontSize: '1.4rem' }}>
                  JEJELY ULTIMATE LIMITED
                </h3>
                <p style={{ color: 'rgba(255,255,255,0.8)', lineHeight: '1.6', marginBottom: '15px', fontSize: '1rem' }}>
                  Your trusted partner for personal microfinance solutions in Nigeria. 
                  Licensed and regulated by the Central Bank of Nigeria.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                  <SafetyOutlined style={{ color: '#52c41a', marginRight: '8px', fontSize: '1rem' }} />
                  <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '1rem' }}>
                    Licensed Microfinance Institution
                  </span>
                </div>
              </div>
            </Col>

            {/* Quick Links */}
            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ color: '#1890ff', marginBottom: '15px', fontSize: '1.2rem' }}>
                  Quick Links
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <Link to="/" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '1rem' }}>
                    Home
                  </Link>
                  <Link to="/about" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '1rem' }}>
                    About Us
                  </Link>
                  <Link to="/faqs" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '1rem' }}>
                    FAQs
                  </Link>
                  <Link to="/contact" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: '1rem' }}>
                    Contact Us
                  </Link>
                </div>
              </div>
            </Col>

            {/* Contact Info */}
            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ color: '#1890ff', marginBottom: '15px', fontSize: '1.2rem' }}>
                  Contact Information
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <PhoneOutlined style={{ color: '#1890ff', marginRight: '10px', fontSize: '1.1rem' }} />
                    <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '1rem' }}>08158120510</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <MailOutlined style={{ color: '#1890ff', marginRight: '10px', fontSize: '1.1rem' }} />
                    <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '1rem' }}>fairlonerhelp@gmail.com</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <WhatsAppOutlined style={{ color: '#25D366', marginRight: '10px', fontSize: '1.1rem' }} />
                    <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '1rem' }}>+234 815 812 0510</span>
                  </div>
                  <div style={{ marginTop: '12px' }}>
                    <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '1rem', margin: '6px 0' }}>
                      <strong>Business Hours:</strong>
                    </p>
                    <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '1rem', margin: '3px 0' }}>
                      Mon-Sun: 8:00 AM - 6:00 PM
                    </p>
                  </div>
                </div>
              </div>
            </Col>
          </Row>


          {/* Copyright */}
          <div style={{ 
            borderTop: '1px solid rgba(255,255,255,0.1)', 
            paddingTop: '20px',
            marginTop: '30px',
            textAlign: 'center',
            color: 'rgba(255,255,255,0.6)'
          }}>
            <p style={{ margin: '0 0 10px 0' }}>
              © 2025 JEJELY ULTIMATE LIMITED. All rights reserved.
            </p>
            
          </div>
        </div>
      </Footer>
    </AntLayout>
  );
};

export default Layout;
